// Sample data: an admin-only view switch for reviewing screens that look bare
// on a quiet server (Records, Friends, Leaderboards). When it is on,
// those screens get generated rows mixed in. Nothing here is ever sent to the
// server and no other player sees it: the rows exist only in this browser tab,
// each carries `sample: true`, renders with a small "Sample" tag, and every
// button on it shows a toast instead of acting.
//
// The generators are pure and deterministic for a seed (built from real
// species, items, relics and shrines, priced from their sell prices and
// rarity), so a review screenshot looks the same twice.
import { SPECIES, DEX_SPECIES, type Rarity } from '../shared/species.ts';
import { ITEMS, RELIC_STATS, rollRelicSides, type Relic, type RelicStat } from '../shared/items.ts';
import { SHRINE_TIERS, type Shrine } from '../shared/shrines.ts';
import { ELEMENT_IDS } from '../shared/elements.ts';
import { OUTFITS } from '../shared/outfits.ts';
import { CONFIG } from '../shared/config.ts';
import { Rng } from '../shared/rng.ts';
import { rollCreature, type Rolls } from '../shared/rolls.ts';
import { totalXp, xpToNext } from '../shared/xp.ts';
import { ratingOf } from '../shared/rating.ts';
import { gradeOf } from '../shared/grade.ts';
import type { BoardId } from '../shared/leaderboards.ts';
import type { FriendRow } from '../shared/social.ts';
import type { BoardRow, CreatureDTO, LeaderboardsDTO, ListingDTO, RecordDTO } from '../shared/protocol.ts';

/** A generated row: shown with a Sample tag, never acted on. */
export type Sampled<T> = T & { sample?: true };

// ---------------- the switch ----------------
const KEY = 'mi-sample';
/** Set from the server's hello (admin flag); the switch does nothing without it. */
let admin = false;
let enabled: boolean | null = null;

export function setSampleAdmin(v: boolean) { admin = v; }
export function isSampleAdmin() { return admin; }

function stored(): boolean {
  try { return globalThis.localStorage?.getItem(KEY) === '1'; } catch { return false; }
}
export function setSampleEnabled(v: boolean) {
  enabled = v;
  try { globalThis.localStorage?.setItem(KEY, v ? '1' : '0'); } catch { /* storage off */ }
}
/** Sample rows show only for an admin who turned the switch on. */
export function sampleOn(): boolean {
  if (!admin) return false;
  if (enabled === null) enabled = stored();
  return enabled;
}

/** The fixed seed review screens use. */
export const SAMPLE_SEED = 20261014;
/** Sample ids are negative so they can never match a real listing. */
export const isSampleId = (id: number) => id < 0;

// ---------------- names ----------------
const FIRST = ['Astrid', 'Kaito', 'Thalia', 'Bjorn', 'Yuki', 'Leander', 'Sigrun', 'Haru', 'Ione', 'Rurik', 'Mei', 'Castor', 'Freya', 'Ren', 'Daphne', 'Ulf', 'Aiko', 'Nikos', 'Solveig', 'Tomo', 'Calla', 'Eirik', 'Nami', 'Orrin'];
const TAIL = ['', '', '', '', 'x', '7', '_', 'o', 'Rune', 'Fox', 'Wolf', 'Tide', 'Ash', '99', 'Kin', 'Moth'];
/** Trainer names for a seed: unique, 3-16 characters. */
export function sampleNames(seed: number, n: number): string[] {
  const rng = new Rng(seed ^ 0x51a3);
  const out = new Set<string>();
  for (let guard = 0; out.size < n && guard < n * 20; guard++) {
    const name = `${rng.pick(FIRST)}${rng.pick(TAIL)}`.slice(0, 16);
    out.add(name);
  }
  return [...out];
}

// ---------------- prices ----------------
const RARITY_BASE: Record<Rarity, number> = { common: 350, uncommon: 1_400, rare: 6_000, epic: 32_000, legendary: 260_000 };
const GRADE_MULT: Record<string, number> = { C: 1, B: 1.7, A: 3.2, S: 9, 'S+': 28 };
/** Two significant figures, like people type prices. */
const round2 = (n: number) => { const p = 10 ** Math.max(0, Math.floor(Math.log10(Math.max(1, n))) - 1); return Math.max(2, Math.round(n / p) * p); };

/** A plausible asking price for a creature: rarity, grade, bloodline, Aura and level. */
export function creaturePrice(species: string, rolls: Rolls, level: number, rng: Rng): number {
  const sp = SPECIES[species];
  const g = gradeOf(ratingOf(rolls, species));
  const base = RARITY_BASE[sp?.rarity ?? 'common'] * (GRADE_MULT[g] ?? 1) * (1 + rolls.bloodline * 0.6) * (rolls.aura ? 25 : 1) * (1 + level / 200);
  return round2(base * rng.range(0.75, 1.4));
}
/** An item's asking price each: its sell price with a player markup. */
export const itemUnit = (sellPrice: number, rng: Rng) => round2(Math.max(20, sellPrice) * rng.range(1.25, 2.6));

// ---------------- generators ----------------
const TRADABLE = ITEMS.filter((i) => i.tradable && i.sellPrice > 0);
const RARITY_W: [Rarity, number][] = [['common', 40], ['uncommon', 30], ['rare', 18], ['epic', 9], ['legendary', 3]];
const byRarity = (r: Rarity) => DEX_SPECIES.filter((id) => SPECIES[id].rarity === r);

/** The best of `k` rolls: records and listed creatures skew toward good catches. */
function bestRolls(rng: Rng, species: string, k: number, auraOneIn = 400): Rolls {
  let best = rollCreature(rng, { auraOneIn, species });
  for (let i = 1; i < k; i++) { const r = rollCreature(rng, { auraOneIn, species }); if (ratingOf(r, species) > ratingOf(best, species)) best = r; }
  return best;
}

function creature(id: number, species: string, rolls: Rolls, level: number, caughtAt: number): CreatureDTO {
  return { id, species, level, xp: 0, rolls, stars: 0, relic: null, caughtAt, ground: '', bound: false, listed: true };
}

/** Market listings and recent sales. */
export function sampleMarket(seed: number, now: number): { listings: Sampled<ListingDTO>[]; sold: Sampled<ListingDTO>[] } {
  const rng = new Rng(seed);
  const names = sampleNames(seed, 18);
  const hold = CONFIG.marketHoldMs, life = CONFIG.marketListingDays * 86_400_000;
  let id = -1;
  const one = (): Sampled<ListingDTO> => {
    const kind = rng.weighted<ListingDTO['kind']>([['creature', 50], ['item', 38], ['relic', 8], ['shrine', 4]]);
    const openAt = rng.chance(0.08) ? now + rng.int(10_000, hold - 5_000) : now - rng.int(60_000, 4 * 86_400_000);
    const base = { id: id--, seller: rng.pick(names), openAt, expires: openAt + life, mine: false, requested: false, sample: true as const };
    if (kind === 'creature') {
      const pool = byRarity(rng.weighted(RARITY_W));
      const species = rng.pick(pool.length ? pool : DEX_SPECIES);
      const rolls = bestRolls(rng, species, rng.int(1, 8), 60);
      const level = rng.int(8, 140);
      const price = creaturePrice(species, rolls, level, rng);
      return { ...base, kind, ref: species, qty: 1, price, unit: price, snapshot: creature(base.id, species, rolls, level, openAt - rng.int(3_600_000, 20 * 86_400_000)) };
    }
    if (kind === 'item') {
      // Cheaper items list far more often than tablets and pearls.
      const it = rng.weighted(TRADABLE.map((i): [typeof i, number] => [i, 1 / Math.sqrt(i.sellPrice)]));
      const unit = itemUnit(it.sellPrice, rng);
      const qty = it.sellPrice >= 50_000 ? rng.int(1, 3) : it.sellPrice >= 5_000 ? rng.int(1, 12) : rng.int(5, 160);
      return { ...base, kind, ref: it.id, qty, price: unit * qty, unit, snapshot: null };
    }
    if (kind === 'relic') {
      const stat: RelicStat = rng.pick(RELIC_STATS).stat;
      const quality = Math.min(100, Math.round(20 + rng.next() ** 1.8 * 78));
      const relic: Relic = { id: `smp-r${-base.id}`, stat, quality, ...(stat === 'element' ? { element: rng.pick(ELEMENT_IDS) } : {}), sides: rollRelicSides('wild', () => rng.next()) };
      const price = round2(1_500 + quality * quality * 28 * rng.range(0.8, 1.3));
      return { ...base, kind, ref: stat, qty: 1, price, unit: price, snapshot: relic };
    }
    const tier = rng.weighted<number>([[0, 70], [1, 24], [2, 6]]);
    const shrine: Shrine = { id: `smp-s${-base.id}`, tier, posts: Array(SHRINE_TIERS[tier].posts).fill(0), active: false };
    const price = round2([45_000, 220_000, 1_400_000][tier] * rng.range(0.8, 1.35));
    return { ...base, kind: 'shrine', ref: String(tier), qty: 1, price, unit: price, snapshot: shrine };
  };
  const listings = Array.from({ length: 34 }, one).sort((a, b) => b.openAt - a.openAt);
  const sold = Array.from({ length: 12 }, one).map((l) => ({ ...l, openAt: Math.min(l.openAt, now - 60_000) })).sort((a, b) => b.openAt - a.openAt);
  return { listings, sold };
}

/** Server records: the best catch of most species, held by a few busy trainers and many casual ones. */
export function sampleRecords(seed: number, now: number, share = 0.72): Sampled<RecordDTO>[] {
  const rng = new Rng(seed ^ 0x7ec0);
  const names = sampleNames(seed, 16);
  // Zipf-ish: the first names hold most records.
  const who = names.map((n, i): [string, number] => [n, 1 / (i + 1)]);
  const out: Sampled<RecordDTO>[] = [];
  for (const species of DEX_SPECIES) {
    if (!rng.chance(share)) continue;
    const rolls = bestRolls(rng, species, rng.int(8, 40), 900);
    out.push({ species, name: rng.weighted(who), score: ratingOf(rolls, species), rolls, at: now - rng.int(3_600_000, 45 * 86_400_000), sample: true });
  }
  return out;
}

/** A friends list: a few online, the rest seen at different times. */
export function sampleFriends(seed: number, now: number, n = 9): Sampled<FriendRow & { seen?: number }>[] {
  const rng = new Rng(seed ^ 0xf00d);
  const tags = ['VALK', 'OLYM', 'KAMI', ''];
  return sampleNames(seed ^ 0x2222, n).map((name, i) => {
    const online = i < 3;
    const tag = rng.pick(tags);
    return {
      name, level: rng.int(12, 420), outfit: rng.pick(OUTFITS).id, online, unread: rng.chance(0.2) ? rng.int(1, 4) : 0,
      giftAt: now - 86_400_000, ...(tag ? { tag } : {}), ...(online ? {} : { seen: now - Math.round(rng.next() ** 2 * 9 * 86_400_000) - 120_000 }), sample: true as const,
    };
  });
}

/** One leaderboard's rows, highest first; `dexMax` caps the Bestiary board. */
export function sampleBoard(id: BoardId, seed: number, now: number, dexMax = DEX_SPECIES.length, n = 40): Sampled<BoardRow>[] {
  const rng = new Rng(seed ^ (id.length * 7919) ^ id.charCodeAt(0));
  const names = sampleNames(seed ^ 0x4444, n + 10);
  const top: Record<BoardId, number> = { level: 460, dex: dexMax, best: 47, auras: 14, week: 38, ladder: 64, arena: 2_450, guild: 2_400_000 };
  const floor: Record<BoardId, number> = { level: 14, dex: 6, best: 26, auras: 1, week: 2, ladder: 1, arena: 950, guild: 40_000 };
  const guilds = ['Valhalla Watch', 'Olympus Rising', 'Kami Lantern', 'Ember Court', 'Tide Wardens', 'Stonebound', 'Frostfang', 'Grove Keepers', 'Storm Choir', 'Shade Ledger'];
  const rows: Sampled<BoardRow>[] = [];
  const count = id === 'guild' ? guilds.length : n;
  for (let i = 0; i < count; i++) {
    const t = i / Math.max(1, count - 1);
    const v = floor[id] + (top[id] - floor[id]) * (1 - t) ** 2.2 * rng.range(0.94, 1);
    const value = id === 'best' ? Math.round(v * 10) / 10 : Math.max(1, Math.round(v));
    if (id === 'guild') { rows.push({ rank: i + 1, name: guilds[i], value, tag: guilds[i].replace(/[^A-Z]/g, '').slice(0, 4) || 'GLD', sub: rng.int(4, 30), sample: true }); continue; }
    const species = rng.pick(DEX_SPECIES);
    rows.push({
      rank: i + 1, name: names[i], value, outfit: rng.pick(OUTFITS).id, lv: rng.int(Math.max(10, Math.round(460 * (1 - t) * 0.5)), Math.max(20, Math.round(460 * (1 - t)))),
      comp: { species, ...(rng.chance(0.06) ? { aura: true } : {}) }, ...(id === 'best' ? { species: rng.pick(DEX_SPECIES), aura: rng.chance(0.05) } : {}),
      ...(rng.chance(0.3) ? { tag: rng.pick(['VALK', 'OLYM', 'KAMI', 'EMBR']) } : {}), sample: true,
      ...(id === 'level' ? { xp: totalXp(value) + Math.floor(rng.range(0, 1) * xpToNext(value)) } : {}),
    });
  }
  rows.sort((a, b) => b.value - a.value).forEach((r, i) => { r.rank = i + 1; });
  return rows;
}

// ---------------- merged views (what the screens read) ----------------
// Cached per seed and hour, so rows don't jump on every render.
const cache = new Map<string, unknown>();
function memo<T>(key: string, make: () => T): T {
  const k = `${key}:${Math.floor(Date.now() / 3_600_000)}`;
  if (!cache.has(k)) { if (cache.size > 40) cache.clear(); cache.set(k, make()); }
  return cache.get(k) as T;
}
/** A stable "now" for generation: the start of this hour, so ages stay sensible. */
const hourNow = () => Math.floor(Date.now() / 3_600_000) * 3_600_000;

/** Records as shown: every real record, and sample ones for species nobody holds. */
export function withSampleRecords(list: RecordDTO[] | null): Sampled<RecordDTO>[] | null {
  if (!sampleOn()) return list;
  const real = list ?? [];
  const have = new Set(real.map((r) => r.species));
  return [...real, ...memo('records', () => sampleRecords(SAMPLE_SEED, hourNow())).filter((r) => !have.has(r.species))];
}

export function withSampleFriends<F extends FriendRow>(list: F[]): Sampled<F | (FriendRow & { seen?: number })>[] {
  if (!sampleOn()) return list;
  const have = new Set(list.map((f) => f.name.toLowerCase()));
  return [...list, ...memo('friends', () => sampleFriends(SAMPLE_SEED, hourNow())).filter((f) => !have.has(f.name.toLowerCase()))];
}

/** A leaderboard as shown: with fewer than 10 real rows, sample rows fill it (ranked together). */
export function withSampleBoard(d: LeaderboardsDTO | undefined, id: BoardId, dexMax?: number): LeaderboardsDTO | undefined {
  if (!sampleOn() || (d && d.rows.length >= 10)) return d;
  const fake = memo(`board-${id}-${dexMax ?? 0}`, () => sampleBoard(id, SAMPLE_SEED, hourNow(), dexMax));
  const rows: Sampled<BoardRow>[] = [...(d?.rows ?? []).map((r) => ({ ...r })), ...fake.map((r) => ({ ...r }))].sort((a, b) => b.value - a.value);
  rows.forEach((r, i) => { r.rank = i && rows[i - 1].value === r.value ? rows[i - 1].rank : i + 1; });
  const at = d?.at ?? Date.now();
  return { at, next: d?.next ?? at + 300_000, id, rows: rows.slice(0, 100), you: d?.you ?? null, total: (d?.total ?? 0) + fake.length, ...(d?.podium ? { podium: d.podium } : {}) };
}

/** True for a generated row. */
export const isSample = (x: unknown): boolean => !!(x && typeof x === 'object' && (x as { sample?: boolean }).sample);

/** The small tag on a generated row. */
export const SAMPLE_TAG = '<span class="smp-tag" title="Generated for review: only you see it">Sample</span>';
/** A sample trainer's name: plain text with the tag; no player popup. */
export const sampleName = (name: string, inner?: string) => `<span class="smp-who"><span class="smp-name" data-act="sample-row">${inner ?? `<b>${name.replace(/[<>&"]/g, '')}</b>`}</span>${SAMPLE_TAG}</span>`;
